import { Box, IconButton, Stack, styled, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import * as React from "react";

export type CommonModalProps<ElementProps = any> = {
    closeModal: () => void;
} & ElementProps;

type OpenModalAction = <ElementProps = any>(
    modalComponent: React.ForwardRefExoticComponent<any>,
    props?: ElementProps
) => void;

export type WrapperModalProps = {
    width: number;
    height?: number | string;
    background?: "paper" | "default";
    title: string | React.ReactNode;
    children: React.ReactNode;
    subheader?: React.ReactNode;
    hideHeader?: boolean;
    footer?: React.ReactNode;
    closeModal: ModalContextContent["closeModal"];
    reference?: React.ForwardedRef<any>;
    headerBackgroundColor?: string;
};

export interface ModalContextContent {
    openModal: OpenModalAction;
    closeModal: () => void;
}

export const ModalContext = React.createContext<ModalContextContent | undefined>(undefined);

export const useModal: () => [
    React.FunctionComponentElement<any> | undefined,
    boolean,
    ModalContextContent["openModal"],
    () => void
] = () => {
    const [isModalOpen, setIsModalOpen] = React.useState(false);
    const [modalElement, setModalElement] = React.useState<React.FunctionComponentElement<any>>();

    const closeModal = React.useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const openModal: ModalContextContent["openModal"] = React.useCallback(
        (modalComponent, elementProps?: any) => {
            setIsModalOpen(true);
            const modalElement = React.createElement(modalComponent, {
                ...elementProps,
                closeModal,
            });
            setModalElement(modalElement);
        },
        [closeModal]
    );

    return [modalElement, isModalOpen, openModal, closeModal];
};

const Modal: React.FC<WrapperModalProps> = (props) => {
    const {
        width,
        height,
        children,
        background = "paper",
        title,
        hideHeader = false,
        subheader = null,
        footer,
        reference = null,
        headerBackgroundColor,
        closeModal,
        ...otherProps
    } = props;
    return (
        <ModalContainer ref={reference} width={width} height={height} background={background} tabIndex={-1} {...otherProps}>
            {!hideHeader && (
                <ModalHeader
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ background: headerBackgroundColor }}
                >
                    {typeof title === "string" ? <Typography variant="h5">{title}</Typography> : title}
                    <IconButton onClick={closeModal}>
                        <CloseIcon />
                    </IconButton>
                </ModalHeader>
            )}
            {subheader}
            <ModalBody
                className="MuiModal-body"
                sx={(theme) => ({ ...theme.helpers(theme).scrollableContainer, height: "auto", position: "relative" })}
            >
                {children}
            </ModalBody>
            {footer && <ModalFooter>{footer}</ModalFooter>}
        </ModalContainer>
    );
};

export default Modal;

const ModalContainer = styled(Stack)<{
    width: WrapperModalProps["width"];
    height?: WrapperModalProps["height"];
    background?: WrapperModalProps["background"];
}>(({ width, height, background, theme }) => ({
    ...theme.helpers(theme).scrollBar,
    background: background === "paper" ? theme.palette.background.paper : theme.palette.background.default,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: "12px",
    width: `${width}px`,
    maxHeight: "90vh",
    height: height || "auto",
    display: "flex",
    "&:focus-visible": {
        outline: "none",
    },
}));

const ModalFooter = styled(Box)(({ theme }) => ({
    padding: theme.spacing(3),
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const ModalHeader = styled(Stack, {
    shouldForwardProp: (prop) => prop !== "headerTitle",
})<{
    headerTitle?: boolean;
}>(({ theme, headerTitle }) => ({
    padding: headerTitle ? theme.spacing(3) : theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
}));
const ModalBody = styled(Stack)(({ theme }) => ({
    padding: theme.spacing(2),
}));
